import { TAppDispatch, TGetState } from 'store/types'
import slice from 'store/features/projectData/slice'
import { PROCESSING_MEDIA_ITEMS, PROJECT_MEDIA_TYPES } from 'consts'
import {
  IMediaItem,
  ITextData,
  IUploadMediaItem,
  MediaStatus,
  TPartialMediaItem
} from 'types/media'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { captureException } from '@sentry/react'
import {
  getAiImagesByIds_API,
  getAIVideoProjects_API,
  uploadFile_API
} from 'api'
import { updateMediaItemWithAIImageData } from 'utils/aiImage'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { ErrorKeys } from 'consts/errorKeys'
import { checkIfAIVideoIsGenerating } from 'containers/SubSidebar/AIVideoContent/utils'
import { AIVideoSocketMessageStatus } from 'types/aiVideo'
import {
  calculateMediaRect,
  calcTextRectForAspectRatioSwitch,
  mapMediaInputAndOutput,
  preserveScaleMediaRect,
  resolveCompletedMediaRect
} from 'utils/media'
import { IDimensions } from 'types'
import { FIT } from 'consts/canvas'
import {
  createDuplicatedItem,
  sortArraysByKey
} from 'store/features/projectData/utils'
import { resolveGapZIndex } from 'utils/timelineZIndex'
import { isBlobUrl, isTextMediaItem } from 'utils'
import { IAddMediaItemsPayload } from 'store/types/projectData'
import { withAppThunks } from 'store/with-app-thunks'
import { handleApiError } from 'store/utils'
import {
  getItemFromLocalStorage,
  removeLoadingProgressFromLocalStorage
} from 'utils/localStorage'
import { generationProgressService } from 'services/generationProgress'
import {
  ICanvasObject,
  ITextMetadata,
  WebWoodEngine
} from '@renderforest/webwood-engine'
import { getItemsToUpdate, waitForTexObjectDimensionsUpdate } from './utils'
import { convertMediaItemsToCanvasObjects } from '@renderforest/v2ww-data-converter'
import { logError } from 'utils/logger'

const { actions } = slice

export interface IMediaFile {
  id: string
  filePath: string
  width: number
  height: number
  duration?: number
  smallFilePath?: string
}

const uploadMediaFiles = async (
  medias: IUploadMediaItem[]
): Promise<Array<IMediaFile>> => {
  const formData = new FormData()

  medias.forEach(media => {
    formData.append('file', media.file)
  })

  const mediaUploadResponse = await uploadFile_API(formData)
  return mapMediaInputAndOutput(medias, mediaUploadResponse)
}

const uploadMediasToProject = (medias: IUploadMediaItem[]) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: TGetState) => {
      const uploadedMedias = await uploadMediaFiles(medias)

      const state = getState()
      const { width: projectWidth, height: projectHeight } =
        projectDataSelectors.defaultDimensions(state)
      const mediaItems = projectDataSelectors.mediaItems(state)

      const uploadedMediasWithDimensions = uploadedMedias.map(uploadedMedia => {
        const currentMediaItem = mediaItems.find(
          item => item.id === uploadedMedia.id
        )

        const mediaItemUpdates: TPartialMediaItem = {
          meta: {
            path: uploadedMedia.filePath,
            status: MediaStatus.OK
          }
        }

        if (
          currentMediaItem &&
          (currentMediaItem.width === 0 || currentMediaItem.height === 0)
        ) {
          const mediaRect = calculateMediaRect({
            mediaDimensions: {
              width: uploadedMedia.width,
              height: uploadedMedia.height
            },
            mode: FIT,
            canvasDimensions: {
              width: projectWidth,
              height: projectHeight
            },
            rotation: 0
          })

          mediaItemUpdates.meta.originalWidth = uploadedMedia.width
          mediaItemUpdates.meta.originalHeight = uploadedMedia.height
          mediaItemUpdates.width = mediaRect.width
          mediaItemUpdates.height = mediaRect.height
          mediaItemUpdates.coordinates = {
            x: mediaRect.x,
            y: mediaRect.y
          }

          if (uploadedMedia.smallFilePath) {
            mediaItemUpdates.meta.previewPath = uploadedMedia.smallFilePath
          }
        }

        return {
          id: uploadedMedia.id,
          updates: mediaItemUpdates,
          status: MediaStatus.OK
        }
      })

      dispatch(actions.updateUploadedMediaItems(uploadedMediasWithDimensions))
      dispatch(
        appThunks.recentlyUsedMediaThunks.updateRecentlyUsedItemsOnUpload(
          uploadedMedias
        )
      )
    }
  )

const uploadMedias = (medias: IUploadMediaItem[]) =>
  withAppThunks(
    appThunks =>
      async (dispatch: TAppDispatch): Promise<IMediaFile[]> => {
        const uploadedMedias = await uploadMediaFiles(medias)

        dispatch(
          appThunks.recentlyUsedMediaThunks.updateRecentlyUsedItemsOnUpload(
            uploadedMedias
          )
        )

        return uploadedMedias
      }
  )

const duplicateVisualMediaItem =
  (id: string) => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)
    const mediaItem = mediaItems.find(item => item.id === id)

    if (!mediaItem || isBlobUrl(mediaItem.meta.path)) {
      return
    }

    const duplicatedItem = createDuplicatedItem<IMediaItem>(
      mediaItems,
      id,
      'zIndex',
      'startTime'
    )

    if (duplicatedItem) {
      dispatch(actions.addMediaItems({ value: [duplicatedItem] }))
    }

    return duplicatedItem?.id
  }

const removeVisualMediaItem = (id: string) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()

    const mediaItems = projectDataSelectors.mediaItems(state)
    const mediaItem = mediaItems.find(item => item.id === id)!
    const assetId = mediaItem.assetId
    const itemsCountWithTargetAssetId = mediaItems.filter(
      item => item.assetId === assetId
    ).length
    if (itemsCountWithTargetAssetId === 1) {
      if (mediaItem.type === PROJECT_MEDIA_TYPES.aiImage) {
        dispatch(appThunks.cacheThunks.removeFromAiImageCache([assetId]))
        dispatch(appThunks.aiImageThunks.resetGenerateForm())
      } else if (mediaItem.type === PROJECT_MEDIA_TYPES.aiVideo) {
        dispatch(appThunks.cacheThunks.removeFromAiVideoCache([assetId]))
        dispatch(appThunks.aiVideoThunks.clearGenerationParams())
      }
    }
    dispatch(actions.removeVisualMediaItem(id))
  })

const syncPendingAiImageData =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const projectDimensions = projectDataSelectors.defaultDimensions(state)
    const pendingAiImgMediaItems =
      projectDataSelectors.pendingAiImgMediaItems(state)

    if (pendingAiImgMediaItems.length === 0) {
      return
    }

    const assetIds = pendingAiImgMediaItems
      .map(item => Number(item.assetId))
      .filter(id => !isNaN(id))

    if (assetIds.length === 0) {
      return
    }

    try {
      const response = await getAiImagesByIds_API({ ids: assetIds })
      const images = response.data

      images.forEach(image => {
        const { width, height } = image
        const firstImage = image.images[0]
        const { id, filePath } = firstImage
        const mediaItem = pendingAiImgMediaItems.find(
          item => item.assetId === id.toString()
        )

        if (mediaItem) {
          dispatch(
            actions.updateMediaItem(
              updateMediaItemWithAIImageData(
                mediaItem,
                filePath,
                width,
                height,
                {
                  width: projectDimensions.width,
                  height: projectDimensions.height
                }
              )
            )
          )
        }
      })
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }
  }

const syncPendingAIVideoData =
  () => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const pendingAIVideoMediaItems =
      projectDataSelectors.pendingAIVideoMediaItems(state)
    const defaultDimensions = projectDataSelectors.defaultDimensions(state)

    if (pendingAIVideoMediaItems.length === 0) {
      return
    }

    const assetIds = pendingAIVideoMediaItems
      .filter(item => !!Number(item.assetId))
      .map(item => Number(item.assetId))

    try {
      const response = await getAIVideoProjects_API({ projectIds: assetIds })
      const videos = response.data

      videos.forEach(video => {
        const { id, videoUrl, videoDurationSeconds, status } = video
        const timelineItem = pendingAIVideoMediaItems.find(
          item =>
            checkIfAIVideoIsGenerating(item) && item.assetId === String(id)
        )
        if (timelineItem && status === AIVideoSocketMessageStatus.COMPLETED) {
          // Video is completed, update the timeline item. Preserve the item's
          // current width/height/coordinates verbatim when the new asset keeps
          // the same aspect ratio so any user resize/reposition survives
          // completion. When aspect ratio changes, fall back to a fresh FIT so
          // the new asset isn't distorted by the old bounding box.
          const { width, height } = video
          const hasDimensions = !!width && !!height

          const originalDimensions = hasDimensions
            ? { originalWidth: width, originalHeight: height }
            : {}

          let rectUpdates: Partial<IMediaItem> = {}
          if (hasDimensions) {
            const resolvedRect = resolveCompletedMediaRect({
              currentWidth: timelineItem.width,
              currentHeight: timelineItem.height,
              currentCoordinates: timelineItem.coordinates,
              newOriginal: { width, height },
              canvasDimensions: defaultDimensions,
              rotation: timelineItem.rotation
            })
            rectUpdates = {
              width: resolvedRect.width,
              height: resolvedRect.height,
              coordinates: { x: resolvedRect.x, y: resolvedRect.y }
            }
          }

          dispatch(
            actions.updateMediaItem({
              id: timelineItem.id,
              updates: {
                duration: videoDurationSeconds,
                ...rectUpdates,
                meta: {
                  ...timelineItem.meta,
                  ...originalDimensions,
                  path: videoUrl,
                  originalDuration: videoDurationSeconds,
                  status: MediaStatus.OK
                }
              }
            })
          )
          removeLoadingProgressFromLocalStorage(timelineItem.id)
        }
      })
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    }
  }

const updateMediaAfterGeneration = (
  payload: {
    id: string
    updates: Partial<IMediaItem>
  },
  config?: {
    isUndoable?: boolean
  }
) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const { id, updates } = payload
    const { isUndoable = true } = config || {}
    if (isUndoable) {
      dispatch(actions.updateMediaItem({ id, updates }))
    } else {
      dispatch(
        actions.updateMediaItemWithoutUndoSave({
          id,
          updates,
          status: MediaStatus.OK,
          isCheckAssetId: false
        })
      )
    }

    const state = getState()
    const mediaItem = state.projectData.present.mediaItems.find(
      item => item.id === id
    )

    if (mediaItem && mediaItem.meta.status !== MediaStatus.PENDING) {
      dispatch(appThunks.recentlyUsedMediaThunks.addNewItems([mediaItem]))
    }
  })

const addMediaItems = ({
  value,
  config = { applySelection: true, isUndoable: true }
}: IAddMediaItemsPayload<IMediaItem>) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    dispatch(actions.addMediaItems({ value, config }))

    const nonPendingMediaItems = value.filter(
      item => item.meta.status !== MediaStatus.PENDING
    )

    dispatch(
      appThunks.recentlyUsedMediaThunks.addNewItems(nonPendingMediaItems)
    )
    if (!config?.applySelection || value.length === 0) {
      return
    }
    dispatch(
      appThunks.editorThunks.selectSingleTimelineItem({
        id: value[0].id,
        type: value[0].type
      })
    )
  })

const uploadFromMediaLibrary = (payload: IAddMediaItemsPayload<IMediaItem>) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch) => {
    dispatch(actions.uploadFromMediaLibrary(payload))
    dispatch(appThunks.recentlyUsedMediaThunks.addNewItems(payload.value))
    const firstItem = payload.value[0]
    if (!firstItem) {
      return
    }
    dispatch(
      appThunks.editorThunks.selectSingleTimelineItem({
        id: firstItem.id,
        type: firstItem.type
      })
    )
  })

// Recalculate all media sizes/positions to FIT the current canvas dimensions.
// `oldCanvas` is the canvas dimensions BEFORE the switch so that we can
// preserve the user's manual resize/reposition relative to the previous FIT
// baseline.
const refitAllMediaToCanvas =
  (oldCanvas: IDimensions) => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const { width: canvasWidth, height: canvasHeight } =
      projectDataSelectors.defaultDimensions(state)
    const mediaItems = projectDataSelectors.mediaItems(state)

    if (!mediaItems || mediaItems.length === 0) return

    const newCanvas = { width: canvasWidth, height: canvasHeight }

    const updates = mediaItems.map(item => {
      const isText = isTextMediaItem(item.type)
      if (isText) {
        if (item.width === 0 || item.height === 0) return null

        const rect = calcTextRectForAspectRatioSwitch(
          item,
          canvasWidth,
          canvasHeight
        )

        return {
          id: item.id,
          updates: {
            width: rect.width,
            height: rect.height,
            coordinates: { x: rect.x, y: rect.y },
            meta: {
              textData: {
                ...item.meta.textData!,
                fontSize: rect.fontSize,
                strokeThickness: rect.strokeThickness
              }
            }
          } as TPartialMediaItem
        }
      }
      const originalWidth = item.meta.originalWidth || item.width
      const originalHeight = item.meta.originalHeight || item.height

      const rect = preserveScaleMediaRect({
        currentWidth: item.width,
        currentHeight: item.height,
        currentCoordinates: item.coordinates,
        oldOriginal: { width: originalWidth, height: originalHeight },
        newOriginal: { width: originalWidth, height: originalHeight },
        oldCanvas,
        newCanvas,
        rotation: item.rotation
      })

      return {
        id: item.id,
        updates: {
          width: rect.width,
          height: rect.height,
          coordinates: { x: rect.x, y: rect.y },
          meta: {}
        } as TPartialMediaItem,
        status: MediaStatus.OK
      }
    })

    const validUpdates = updates.filter(Boolean) as NonNullable<
      (typeof updates)[number]
    >[]

    if (validUpdates.length > 0) {
      dispatch(actions.updateUploadedMediaItems(validUpdates))
    }
  }

const handleFailedGenerationForMediaItem = (targetMediaItemId: string) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    if (targetMediaItemId) {
      generationProgressService.stop({
        mediaItemId: targetMediaItemId,
        shouldRemoveFromStorage: true
      })
      const processingItems = projectDataSelectors.processingItems(getState())
      const processingItemsFromLocalStorage =
        getItemFromLocalStorage(PROCESSING_MEDIA_ITEMS) || []

      const allProcessingItems = [
        ...processingItems,
        ...processingItemsFromLocalStorage
      ]
      const existingProcessingItem = allProcessingItems.find(
        item => item.id === targetMediaItemId
      )
      if (existingProcessingItem) {
        // Restore processing item’s previous data back to media item,
        // except the startTime(in case scenes were reordered during the generation)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { startTime, ...dataToRestore } = existingProcessingItem
        dispatch(
          appThunks.projectDataThunks.updateMediaItem({
            id: targetMediaItemId,
            updates: dataToRestore
          })
        )
        dispatch(
          appThunks.editorThunks.reselectActiveTimelineMediaItem(
            existingProcessingItem
          )
        )
        dispatch(
          appThunks.projectDataThunks.removeFromProcessingItems(
            existingProcessingItem.id
          )
        )
      } else {
        dispatch(
          appThunks.projectDataThunks.removeVisualMediaItem(targetMediaItemId)
        )
        dispatch(appThunks.editorThunks.setAsideOverlayContent(null))
      }
    }
  })

// Inserts new items at a given position in the timeline and pushes any
// trailing items that would otherwise overlap. When `targetGapAfterLineIndex`
// is supplied (gap drop), the targetZIndex is computed from it — bumping
// existing items if the new row needs to slot mid-stack.
const adjustTimelineForNewItems = (
  newItems: IMediaItem[],
  previousItemId?: string,
  targetZIndex?: number,
  targetGapAfterLineIndex?: number | null
) =>
  withAppThunks(appThunks => (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const mediaItems = projectDataSelectors.mediaItems(state)

    let lineZIndex: number
    let currentMediaitems = mediaItems
    const isGapDrop =
      targetGapAfterLineIndex !== undefined && targetGapAfterLineIndex !== null

    if (isGapDrop) {
      const { lineZIndex: gapZ, bumpedMediaItems } = resolveGapZIndex(
        mediaItems,
        targetGapAfterLineIndex
      )
      lineZIndex = gapZ

      if (bumpedMediaItems) {
        dispatch(
          appThunks.projectDataThunks.setMediaItems({ value: bumpedMediaItems })
        )
        currentMediaitems = bumpedMediaItems
      }
    } else {
      lineZIndex = targetZIndex ?? newItems[0].zIndex
    }

    const adjustedNewItems = newItems.map(item => ({
      ...item,
      zIndex: lineZIndex
    }))

    // For gap drops the new line is empty, so cascade isn't needed and
    // `previousItemId` is meaningless.
    const currentLineItems = isGapDrop
      ? []
      : sortArraysByKey(
          'startTime',
          currentMediaitems.filter(item => item.zIndex === lineZIndex)
        )

    const prevItemIndex =
      !isGapDrop && previousItemId
        ? currentLineItems.findIndex(item => item.id === previousItemId)
        : -1

    const newItemsFirstIndex = prevItemIndex + 1
    const newItemsLastIndex = newItemsFirstIndex + adjustedNewItems.length - 1
    currentLineItems.splice(newItemsFirstIndex, 0, ...adjustedNewItems)

    const lastNewItem = adjustedNewItems[adjustedNewItems.length - 1]

    const itemsToUpdate = getItemsToUpdate(
      lastNewItem,
      newItemsLastIndex,
      currentLineItems
    )

    const hasItemsToUpdate = itemsToUpdate.length > 0

    if (hasItemsToUpdate) {
      dispatch(appThunks.projectDataThunks.updateTimelineItems(itemsToUpdate))
    }

    adjustedNewItems.forEach(newItem => {
      dispatch(
        appThunks.projectDataThunks.addMediaItems({
          value: [newItem],
          config: { applySelection: false, isUndoable: !hasItemsToUpdate }
        })
      )
    })
  })

const updateTextSizeParams =
  (id: string, fontSize: number, strokeThickness: number) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const engine = WebWoodEngine.getInstance()
    if (!engine) {
      logError('Engine not found')
      return
    }
    const state = getState()
    const mediaItem = projectDataSelectors
      .mediaItems(state)
      .find(item => item.id === id)
    if (!mediaItem || !mediaItem.meta.textData) {
      logError('Media item not found', mediaItem)
      return
    }
    const textData: ITextData = mediaItem.meta.textData
    const newConfig = convertMediaItemsToCanvasObjects([
      mediaItem
    ])[0] as ICanvasObject<ITextMetadata>

    if (!newConfig.metadata?.style) {
      newConfig.metadata.style = {
        fontSize,
        fillColor: textData.fillColor,
        stroke: {
          color: textData.strokeColor,
          thickness: strokeThickness
        }
      }
    }
    newConfig.metadata.style.fontSize = fontSize
    newConfig.metadata.style.stroke.thickness = strokeThickness
    const data = await waitForTexObjectDimensionsUpdate(newConfig)
    if (!data) return
    dispatch(
      actions.updateMediaItem({
        id,
        updates: {
          width: data.width,
          height: data.height,
          meta: {
            ...mediaItem.meta,
            textData: {
              ...textData,
              fontSize,
              strokeThickness
            }
          }
        }
      })
    )
  }

export const projectDataMediaThunks = {
  uploadMedias,
  updateTextSizeParams,
  uploadMediasToProject,
  duplicateVisualMediaItem,
  updateMediaAfterGeneration,
  uploadFromMediaLibrary,
  addMediaItems,
  removeVisualMediaItem,
  syncPendingAiImageData,
  syncPendingAIVideoData,
  refitAllMediaToCanvas,
  handleFailedGenerationForMediaItem,
  adjustTimelineForNewItems
}
